import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    User,
    Phone,
    FileText,
    Building2,
    MapPin,
    CreditCard,
    Briefcase,
    ShieldCheck,
    CheckCircle,
    ArrowRight,
    ArrowLeft,
    Loader2,
    AlertCircle,
    Check,
    X,
    Signature
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { Checkbox } from '@/components/ui/checkbox';
import ViewAsUserBanner from '../Staff/ViewAsUserBanner';

// Validation Helper Functions
const validateCPF = (cpf) => {
    cpf = cpf.replace(/[^\d]+/g, '');
    if (cpf === '') return false;
    if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;
    let add = 0;
    for (let i = 0; i < 9; i++) add += parseInt(cpf.charAt(i)) * (10 - i);
    let rev = 11 - (add % 11);
    if (rev === 10 || rev === 11) rev = 0;
    if (rev !== parseInt(cpf.charAt(9))) return false;
    add = 0;
    for (let i = 0; i < 10; i++) add += parseInt(cpf.charAt(i)) * (11 - i);
    rev = 11 - (add % 11);
    if (rev === 10 || rev === 11) rev = 0;
    if (rev !== parseInt(cpf.charAt(10))) return false;
    return true;
};

const validateCNPJ = (cnpj) => {
    cnpj = cnpj.replace(/[^\d]+/g, '');
    if (cnpj === '') return false;
    return cnpj.length === 14;
};


const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
};

const validatePhone = (phone) => {
    const cleanPhone = phone.replace(/[^\d]+/g, '');
    return cleanPhone.length >= 10 && cleanPhone.length <= 11;
};

// Masking Helper Functions
const maskCPF = (value) => {
    return value
        .replace(/\D/g, '')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})/, '$1-$2')
        .replace(/(-\d{2})\d+?$/, '$1');
};

const maskCNPJ = (value) => {
    return value
        .replace(/\D/g, '')
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2')
        .replace(/(-\d{2})\d+?$/, '$1');
};

const maskPhone = (value) => {
    return value
        .replace(/\D/g, '')
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2')
        .replace(/(-\d{4})\d+?$/, '$1');
};

const maskCNAE = (value) => {
    return value
        .replace(/\D/g, '')
        .replace(/(\d{4})(\d)/, '$1-$2')
        .replace(/-(\d{1})(\d)/, '-$1/$2')
        .replace(/(\/\d{2})\d+?$/, '$1');
};



const StaffRegisterPage = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { registerStaff, updateStaffData, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [formData, setFormData] = useState({
        discordId: '',
        personalData: {
            fullName: '',
            cpf: '',
            birthDate: '',
            phone: '',
            email: ''
        },
        businessData: {
            cnpj: '',
            businessName: '',
            openingDate: '',
            primaryCnae: '',
            fullAddress: ''
        },
        bankingData: {
            bank: '',
            accountType: 'corrente',
            branch: '',
            account: '',
            holderName: '',
            pixKeyType: 'cpf',
            pixKey: ''
        },
        contractData: {
            jobFunction: '',
            contractTerm: '3 meses',
            contractValue: '',
            serviceDescription: ''
        },
        terms: {
            meiDeclaration: false,
            noVincularion: false,
            confidentiality: false,
            lgpd: false,
            quality: false,
            competence: false,
            deadlines: false,
            noConflict: false,
            postContractConfidentiality: false,
            truthfulInfo: false,
            dataUpdate: false,
            approvalAwareness: false
        },
        signature: ''
    });

    const [discordData, setDiscordData] = useState(null);
    const [preStaffData, setPreStaffData] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    const [isUpdateMode, setIsUpdateMode] = useState(false);

    useEffect(() => {
        // PROTECTION: If user already has staff details and NOT in update mode, redirect to staff panel
        if (user?.staffDetails && !location.state?.isUpdate) {
            navigate('/staff', { replace: true });
            return;
        }

        if (location.state?.isUpdate && location.state?.currentData) {
            setIsUpdateMode(true);
            const current = location.state.currentData;
            // Pre-fill form with existing data
            setFormData(prev => ({
                ...prev,
                discordId: location.state.discordData?.discordId || user?.discordId,
                personalData: current.personalData || prev.personalData,
                businessData: current.businessData || prev.businessData,
                bankingData: current.bankingData || prev.bankingData,
                contractData: current.contractData || prev.contractData,
                // Allow re-signing or keep signature empty for re-confirmation
                signature: ''
            }));

            // Assume terms are accepted if updating? Or force re-accept? Force re-accept is safer/better.
        } else if (location.state?.discordData && location.state?.preStaffData) {
            setDiscordData(location.state.discordData);
            setPreStaffData(location.state.preStaffData);
            setFormData(prev => ({
                ...prev,
                discordId: location.state.discordData.discordId,
                personalData: {
                    ...prev.personalData,
                    fullName: location.state.discordData.name,
                    email: location.state.discordData.email || ''
                },
                contractData: {
                    ...prev.contractData,
                    jobFunction: location.state.preStaffData.accessLevel,
                    contractValue: location.state.preStaffData.contractValue || '',
                    contractTerm: (location.state.preStaffData.contractDuration || location.state.preStaffData.contractMonths)
                        ? `${location.state.preStaffData.contractDuration || location.state.preStaffData.contractMonths} ${location.state.preStaffData.contractUnit === 'days' ? 'dias' : 'meses'}`
                        : '3 meses',
                    serviceDescription: location.state.preStaffData.serviceDescription || ''
                }
            }));
        } else {
            navigate('/');
        }
    }, [location.state, navigate, user]);

    const steps = [
        { id: 1, title: 'Dados Pessoais', icon: User },
        { id: 2, title: 'Dados da Empresa', icon: Building2 },
        { id: 3, title: 'Dados Bancários', icon: CreditCard },
        { id: 4, title: 'Contrato', icon: Briefcase },
        { id: 5, title: 'Termos MEI', icon: ShieldCheck },
        { id: 6, title: 'Assinatura', icon: Signature }
    ];

    const handleInputChange = (section, field, value) => {
        let maskedValue = value;

        // Apply Masks
        if (field === 'cpf' || (section === 'bankingData' && field === 'pixKey' && formData.bankingData.pixKeyType === 'cpf')) {
            maskedValue = maskCPF(value);
        } else if (field === 'cnpj' || (section === 'bankingData' && field === 'pixKey' && formData.bankingData.pixKeyType === 'cnpj')) {
            maskedValue = maskCNPJ(value);
        } else if (field === 'phone' || (section === 'bankingData' && field === 'pixKey' && formData.bankingData.pixKeyType === 'telefone')) {
            maskedValue = maskPhone(value);
        } else if (field === 'primaryCnae') {
            maskedValue = maskCNAE(value);
        }

        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: maskedValue
            }
        }));
        if (error) setError('');
        if (fieldErrors[field]) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleNext = () => {
        const newFieldErrors = {};

        // Validation Logic
        if (currentStep === 1) {
            const { fullName, cpf, phone, email, birthDate } = formData.personalData;
            if (!fullName) newFieldErrors.fullName = 'Nome completo é obrigatório.';

            if (!cpf) {
                newFieldErrors.cpf = 'CPF é obrigatório.';
            } else if (!validateCPF(cpf)) {
                newFieldErrors.cpf = 'CPF inválido.';
            }

            if (!phone) {
                newFieldErrors.phone = 'Telefone é obrigatório.';
            } else if (!validatePhone(phone)) {
                newFieldErrors.phone = 'Telefone inválido (mínimo 10 dígitos).';
            }

            if (email && !validateEmail(email)) {
                newFieldErrors.email = 'E-mail inválido.';
            }

            if (!birthDate) newFieldErrors.birthDate = 'Data de nascimento é obrigatória.';

            if (Object.keys(newFieldErrors).length > 0) {
                setFieldErrors(newFieldErrors);
                return;
            }
        }

        if (currentStep === 2) {
            const { cnpj, businessName, fullAddress, openingDate, primaryCnae } = formData.businessData;

            if (!cnpj) {
                newFieldErrors.cnpj = 'CNPJ é obrigatório.';
            } else if (!validateCNPJ(cnpj)) {
                newFieldErrors.cnpj = 'CNPJ inválido.';
            }

            if (!businessName) newFieldErrors.businessName = 'Nome empresarial é obrigatório.';
            if (!openingDate) newFieldErrors.openingDate = 'Data de abertura é obrigatória.';
            if (!primaryCnae) newFieldErrors.primaryCnae = 'CNAE Principal é obrigatório.';
            if (!fullAddress) newFieldErrors.fullAddress = 'Endereço completo é obrigatório.';

            if (Object.keys(newFieldErrors).length > 0) {
                setFieldErrors(newFieldErrors);
                return;
            }
        }

        if (currentStep === 3) {
            const { bank, accountType, branch, account, holderName, pixKeyType, pixKey } = formData.bankingData;
            if (!bank) newFieldErrors.bank = 'Banco é obrigatório.';
            if (!branch) newFieldErrors.branch = 'Agência é obrigatória.';
            if (!account) newFieldErrors.account = 'Conta é obrigatória.';
            if (!holderName) newFieldErrors.holderName = 'Nome do titular é obrigatório.';
            if (!pixKey) newFieldErrors.pixKey = 'Chave PIX é obrigatória.';

            // Optional: specifics validation for PIX key based on type
            if (pixKeyType === 'cpf' && !validateCPF(pixKey)) newFieldErrors.pixKey = 'Chave PIX (CPF) inválida.';
            if (pixKeyType === 'cnpj' && !validateCNPJ(pixKey)) newFieldErrors.pixKey = 'Chave PIX (CNPJ) inválida.';
            if (pixKeyType === 'email' && !validateEmail(pixKey)) newFieldErrors.pixKey = 'Chave PIX (E-mail) inválida.';
            if (pixKeyType === 'telefone' && !validatePhone(pixKey)) newFieldErrors.pixKey = 'Chave PIX (Telefone) inválida.';

            if (Object.keys(newFieldErrors).length > 0) {
                setFieldErrors(newFieldErrors);
                return;
            }
        }

        if (currentStep === 4) {
            const { serviceDescription, contractValue } = formData.contractData;
            if (!serviceDescription) {
                setError('Descreva brevemente o serviço prestado.');
                return;
            }
            if (!contractValue) {
                setError('Valor do contrato é obrigatório.');
                return;
            }
        }
        if (currentStep === 5) {
            const allChecked = Object.values(formData.terms).every(val => val === true);
            if (!allChecked) {
                setError('Você deve aceitar todos os termos para prosseguir.');
                return;
            }
        }

        if (currentStep < steps.length) {
            setCurrentStep(prev => prev + 1);
            setError('');
            setFieldErrors({});
        } else {
            handleSubmit();
        }
    };

    const handleStepClick = (stepId) => {
        // Allow going back freely
        if (stepId < currentStep) {
            setCurrentStep(stepId);
            setError('');
            setFieldErrors({});
            return;
        }

        // To go forward, must validate current step
        if (stepId > currentStep) {
            // We only allow clicking to the NEXT step if valid, 
            // but if they want to jump far ahead, they should go one by one.
            // Or we just call handleNext repeatedly? Let's just allow clicking any previous step.
            // For now, let's just stick to letting them go back.
            // Requirement says "permita que o usuário avance e volte", so maybe they can click ahead too.
            // But they must pass validation.
            handleNext();
        }
    };


    const handlePrevious = () => {
        if (currentStep > 1) {
            setCurrentStep(prev => prev - 1);
            setError('');
        }
    };

    const handleSubmit = async () => {
        if (!formData.signature) {
            setError('Por favor, digite seu nome completo para assinar.');
            return;
        }

        if (formData.signature !== formData.personalData.fullName) {
            setError('A assinatura deve ser idêntica ao nome completo informado.');
            return;
        }

        setLoading(true);
        try {
            let result;

            if (isUpdateMode) {
                const detailsToUpdate = {
                    personalData: formData.personalData,
                    businessData: formData.businessData,
                    bankingData: formData.bankingData,
                    contractData: formData.contractData
                };
                result = await updateStaffData(formData.discordId, detailsToUpdate);
            } else {
                // Parse Contract Term
                const term = formData.contractData.contractTerm || '3 meses';
                let duration = 3;
                let unit = 'months';

                if (term.includes('ano')) {
                    duration = parseInt(term) * 12;
                    unit = 'months';
                } else if (term.includes('mes')) {
                    duration = parseInt(term);
                    unit = 'months';
                } else if (term.includes('dias')) {
                    duration = parseInt(term);
                    unit = 'days';
                }

                const payload = {
                    ...formData,
                    contractData: {
                        ...formData.contractData,
                        contractDuration: duration,
                        contractMonths: duration, // For compatibility
                        contractUnit: unit
                    },
                    discordData,
                    preStaffData
                };

                result = await registerStaff(payload);
            }

            if (result.success) {
                // Navigate to staff panel upon success
                navigate('/staff');
            } else {
                setError(result.error || 'Erro ao processar cadastro.');
            }
        } catch (err) {
            setError('Erro interno. Tente novamente mais tarde.');
        } finally {
            setLoading(false);
        }
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-300">Nome Completo</label>
                            <Input
                                value={formData.personalData.fullName}
                                onChange={(e) => handleInputChange('personalData', 'fullName', e.target.value)}
                                className={`bg-white/5 border-white/10 text-white ${fieldErrors.fullName ? 'border-red-500' : ''}`}
                            />
                            {fieldErrors.fullName && <p className="text-xs text-red-400">{fieldErrors.fullName}</p>}
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-300">E-mail</label>
                            <Input
                                type="email"
                                value={formData.personalData.email}
                                onChange={(e) => handleInputChange('personalData', 'email', e.target.value)}
                                className={`bg-white/5 border-white/10 text-white ${fieldErrors.email ? 'border-red-500' : ''}`}
                            />
                            {fieldErrors.email && <p className="text-xs text-red-400">{fieldErrors.email}</p>}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-300">CPF</label>
                                <Input
                                    placeholder="000.000.000-00"
                                    value={formData.personalData.cpf}
                                    onChange={(e) => handleInputChange('personalData', 'cpf', e.target.value)}
                                    className={`bg-white/5 border-white/10 text-white ${fieldErrors.cpf ? 'border-red-500' : ''}`}
                                />
                                {fieldErrors.cpf && <p className="text-xs text-red-400">{fieldErrors.cpf}</p>}
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-300">Data de Nascimento</label>
                                <Input
                                    type="date"
                                    value={formData.personalData.birthDate}
                                    onChange={(e) => handleInputChange('personalData', 'birthDate', e.target.value)}
                                    className={`bg-white/5 border-white/10 text-white ${fieldErrors.birthDate ? 'border-red-500' : ''}`}
                                />
                                {fieldErrors.birthDate && <p className="text-xs text-red-400">{fieldErrors.birthDate}</p>}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-300">Telefone / WhatsApp</label>
                            <Input
                                placeholder="(00) 00000-0000"
                                value={formData.personalData.phone}
                                onChange={(e) => handleInputChange('personalData', 'phone', e.target.value)}
                                className={`bg-white/5 border-white/10 text-white ${fieldErrors.phone ? 'border-red-500' : ''}`}
                            />
                            {fieldErrors.phone && <p className="text-xs text-red-400">{fieldErrors.phone}</p>}
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-300">CNPJ</label>
                                <Input
                                    placeholder="00.000.000/0000-00"
                                    value={formData.businessData.cnpj}
                                    onChange={(e) => handleInputChange('businessData', 'cnpj', e.target.value)}
                                    className={`bg-white/5 border-white/10 text-white ${fieldErrors.cnpj ? 'border-red-500' : ''}`}
                                />
                                {fieldErrors.cnpj && <p className="text-xs text-red-400">{fieldErrors.cnpj}</p>}
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-300">Data de Abertura</label>
                                <Input
                                    type="date"
                                    value={formData.businessData.openingDate}
                                    onChange={(e) => handleInputChange('businessData', 'openingDate', e.target.value)}
                                    className={`bg-white/5 border-white/10 text-white ${fieldErrors.openingDate ? 'border-red-500' : ''}`}
                                />
                                {fieldErrors.openingDate && <p className="text-xs text-red-400">{fieldErrors.openingDate}</p>}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-300">Nome Empresarial / Razão Social</label>
                            <Input
                                value={formData.businessData.businessName}
                                onChange={(e) => handleInputChange('businessData', 'businessName', e.target.value)}
                                className={`bg-white/5 border-white/10 text-white ${fieldErrors.businessName ? 'border-red-500' : ''}`}
                            />
                            {fieldErrors.businessName && <p className="text-xs text-red-400">{fieldErrors.businessName}</p>}
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-300">CNAE Principal</label>
                            <Input
                                placeholder="0000-0/00"
                                value={formData.businessData.primaryCnae}
                                onChange={(e) => handleInputChange('businessData', 'primaryCnae', e.target.value)}
                                className={`bg-white/5 border-white/10 text-white ${fieldErrors.primaryCnae ? 'border-red-500' : ''}`}
                            />
                            {fieldErrors.primaryCnae && <p className="text-xs text-red-400">{fieldErrors.primaryCnae}</p>}
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-300">Endereço Completo</label>
                            <Input
                                placeholder="Rua, Número, Complemento, Bairro, Cidade - UF"
                                value={formData.businessData.fullAddress}
                                onChange={(e) => handleInputChange('businessData', 'fullAddress', e.target.value)}
                                className={`bg-white/5 border-white/10 text-white ${fieldErrors.fullAddress ? 'border-red-500' : ''}`}
                            />
                            {fieldErrors.fullAddress && <p className="text-xs text-red-400">{fieldErrors.fullAddress}</p>}
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-300">Banco</label>
                                <Input
                                    placeholder="Ex: Nubank, Itaú..."
                                    value={formData.bankingData.bank}
                                    onChange={(e) => handleInputChange('bankingData', 'bank', e.target.value)}
                                    className={`bg-white/5 border-white/10 text-white ${fieldErrors.bank ? 'border-red-500' : ''}`}
                                />
                                {fieldErrors.bank && <p className="text-xs text-red-400">{fieldErrors.bank}</p>}
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-300">Tipo de Conta</label>
                                <Select
                                    value={formData.bankingData.accountType}
                                    onValueChange={(val) => handleInputChange('bankingData', 'accountType', val)}
                                >
                                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-gray-900 border-white/10 text-white">
                                        <SelectItem value="corrente">Corrente</SelectItem>
                                        <SelectItem value="poupanca">Poupança</SelectItem>
                                        <SelectItem value="pagamento">Pagamento</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-300">Agência</label>
                                <Input
                                    value={formData.bankingData.branch}
                                    onChange={(e) => handleInputChange('bankingData', 'branch', e.target.value)}
                                    className={`bg-white/5 border-white/10 text-white ${fieldErrors.branch ? 'border-red-500' : ''}`}
                                />
                                {fieldErrors.branch && <p className="text-xs text-red-400">{fieldErrors.branch}</p>}
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-300">Conta</label>
                                <Input
                                    value={formData.bankingData.account}
                                    onChange={(e) => handleInputChange('bankingData', 'account', e.target.value)}
                                    className={`bg-white/5 border-white/10 text-white ${fieldErrors.account ? 'border-red-500' : ''}`}
                                />
                                {fieldErrors.account && <p className="text-xs text-red-400">{fieldErrors.account}</p>}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-300">Nome do Titular</label>
                            <Input
                                value={formData.bankingData.holderName}
                                onChange={(e) => handleInputChange('bankingData', 'holderName', e.target.value)}
                                className={`bg-white/5 border-white/10 text-white ${fieldErrors.holderName ? 'border-red-500' : ''}`}
                            />
                            {fieldErrors.holderName && <p className="text-xs text-red-400">{fieldErrors.holderName}</p>}
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2 lg:col-span-1">
                                <label className="text-sm font-medium text-gray-300">Tipo Chave PIX</label>
                                <Select
                                    value={formData.bankingData.pixKeyType}
                                    onValueChange={(val) => handleInputChange('bankingData', 'pixKeyType', val)}
                                >
                                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-gray-900 border-white/10 text-white">
                                        <SelectItem value="cpf">CPF</SelectItem>
                                        <SelectItem value="cnpj">CNPJ</SelectItem>
                                        <SelectItem value="email">E-mail</SelectItem>
                                        <SelectItem value="telefone">Telefone</SelectItem>
                                        <SelectItem value="aleatoria">Aleatória</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2 lg:col-span-2">
                                <label className="text-sm font-medium text-gray-300">Chave PIX</label>
                                <Input
                                    value={formData.bankingData.pixKey}
                                    onChange={(e) => handleInputChange('bankingData', 'pixKey', e.target.value)}
                                    className={`bg-white/5 border-white/10 text-white ${fieldErrors.pixKey ? 'border-red-500' : ''}`}
                                />
                                {fieldErrors.pixKey && <p className="text-xs text-red-400">{fieldErrors.pixKey}</p>}
                            </div>
                        </div>
                    </div>
                );
            case 4:
                return (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-300">Função Contratada</label>
                                <Input
                                    value={formData.contractData.jobFunction}
                                    disabled
                                    className="bg-white/5 border-white/10 text-gray-500"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-300">Prazo do Contrato</label>
                                <Select
                                    value={formData.contractData.contractTerm}
                                    onValueChange={(val) => handleInputChange('contractData', 'contractTerm', val)}
                                >
                                    <SelectTrigger
                                        className={`bg-white/5 border-white/10 text-white ${(preStaffData?.contractDuration || preStaffData?.contractMonths) ? 'opacity-70 cursor-not-allowed' : ''}`}
                                        disabled={!!(preStaffData?.contractDuration || preStaffData?.contractMonths)}
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-gray-900 border-white/10 text-white">
                                        <SelectItem value="1 mes">1 mês</SelectItem>
                                        <SelectItem value="3 meses">3 meses</SelectItem>
                                        <SelectItem value="6 meses">6 meses</SelectItem>
                                        <SelectItem value="1 ano">1 ano</SelectItem>
                                        {formData.contractData.contractTerm && !['1 mes', '3 meses', '6 meses', '1 ano'].includes(formData.contractData.contractTerm) && (
                                            <SelectItem value={formData.contractData.contractTerm}>{formData.contractData.contractTerm}</SelectItem>
                                        )}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-300">Valor do Contrato (R$)</label>
                            <Input
                                value={formData.contractData.contractValue}
                                onChange={(e) => handleInputChange('contractData', 'contractValue', e.target.value)}
                                disabled={!!preStaffData?.contractValue} // Disable if pre-filled
                                className={`bg-white/5 border-white/10 text-white ${preStaffData?.contractValue ? 'opacity-70 cursor-not-allowed' : ''}`}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-300">Descrição do Serviço</label>
                            <textarea
                                value={formData.contractData.serviceDescription}
                                onChange={(e) => handleInputChange('contractData', 'serviceDescription', e.target.value)}
                                disabled={!!preStaffData?.serviceDescription}
                                className={`w-full h-32 bg-white/5 border border-white/10 rounded-lg p-3 text-white focus:outline-none focus:ring-1 focus:ring-blue-500 ${preStaffData?.serviceDescription ? 'opacity-70 cursor-not-allowed' : ''}`}
                                placeholder="Descreva brevemente as atividades que serão realizadas..."
                            />
                        </div>
                    </div>
                );
            case 5:
                return (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-800">
                        {[
                            { id: 'meiDeclaration', text: '1. Declaro que sou Microempreendedor Individual (MEI) devidamente registrado e em situação regular perante os órgãos competentes.' },
                            { id: 'noVincularion', text: '2. Declaro que abro mão de qualquer vínculo empregatício com a Tronos Capital, reconhecendo que a relação será estritamente comercial entre empresas.' },
                            { id: 'confidentiality', text: '3. Comprometo-me a manter absoluta confidencialidade sobre todas as informações, dados, processos e estratégias da Tronos Capital.' },
                            { id: 'lgpd', text: '4. Declaro estar ciente e concordo com as políticas de proteção de dados pessoais (LGPD) da Tronos Capital.' },
                            { id: 'quality', text: '5. Comprometo-me a executar os serviços contratados com qualidade, pontualidade e profissionalismo.' },
                            { id: 'competence', text: '6. Declaro que possuo as competências técnicas necessárias para executar a função contratada.' },
                            { id: 'deadlines', text: '7. Comprometo-me a cumprir os prazos estabelecidos no contrato e comunicar eventuais impedimentos com antecedência.' },
                            { id: 'noConflict', text: '8. Declaro que não possuo conflitos de interesse que possam prejudicar a execução dos serviços.' },
                            { id: 'postContractConfidentiality', text: '9. Comprometo-me a não divulgar informações confidenciais mesmo após o término do contrato.' },
                            { id: 'truthfulInfo', text: '10. Declaro que as informações fornecidas neste cadastro são verdadeiras e completas.' },
                            { id: 'dataUpdate', text: '11. Comprometo-me a manter meus dados atualizados e comunicar qualquer alteração relevante.' },
                            { id: 'approvalAwareness', text: '12. Declaro estar ciente de que o cadastro será submetido à análise e aprovação da Tronos Capital.' }
                        ].map((term) => (
                            <div key={term.id} className="flex items-start gap-4 p-3 bg-white/5 rounded-lg border border-white/5 hover:border-white/10 transition-colors group">
                                <Checkbox
                                    id={term.id}
                                    checked={formData.terms[term.id]}
                                    onCheckedChange={(checked) => {
                                        setFormData(prev => ({
                                            ...prev,
                                            terms: { ...prev.terms, [term.id]: checked }
                                        }));
                                    }}
                                    className="mt-1 border-white/20 data-[state=checked]:bg-blue-600"
                                />
                                <label
                                    htmlFor={term.id}
                                    className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors cursor-pointer"
                                >
                                    {term.text}
                                </label>
                            </div>
                        ))}
                    </div>
                );
            case 6:
                return (
                    <div className="space-y-6">
                        <div className="p-6 bg-blue-500/5 border border-blue-500/10 rounded-2xl text-center space-y-4">
                            <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto">
                                <Signature className="h-8 w-8 text-blue-400" />
                            </div>
                            <h3 className="text-xl font-bold text-white">Assinatura Digital</h3>
                            <p className="text-sm text-gray-400">
                                Para finalizar seu cadastro, digite seu nome completo no campo abaixo.
                                Esta ação possui valor de assinatura digital confirmatória.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-300">Nome para Assinatura</label>
                            <Input
                                placeholder="Digite seu nome completo igual aos dados pessoais"
                                value={formData.signature}
                                onChange={(e) => setFormData(prev => ({ ...prev, signature: e.target.value }))}
                                className="bg-white/5 border-white/10 text-white h-14 text-center font-serif italic text-lg"
                            />
                        </div>

                        {formData.signature && formData.signature === formData.personalData.fullName ? (
                            <div className="flex items-center justify-center gap-2 text-green-400 text-sm animate-in fade-in zoom-in duration-300">
                                <CheckCircle className="h-4 w-4" />
                                Assinatura validada com os dados pessoais
                            </div>
                        ) : formData.signature ? (
                            <div className="flex items-center justify-center gap-2 text-yellow-500 text-sm">
                                <AlertCircle className="h-4 w-4" />
                                O nome deve ser igual ao informado no Passo 1
                            </div>
                        ) : null}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-[#020617] relative overflow-hidden flex items-center justify-center p-4">
            <ViewAsUserBanner />
            {/* Background Effects */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-20" />

            {/* Logo Section */}
            <div className="absolute top-8 left-1/2 -translate-x-1/2 text-center z-20">
                <h1 className="text-xl font-bold tracking-tight text-white/90">
                    GRUPO CAPITAL
                </h1>
                <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-blue-500 to-transparent my-1" />
                <p className="text-[10px] font-medium tracking-[0.2em] text-blue-400 uppercase">
                    by Tronos.Space
                </p>
            </div>

            <div className="w-full max-w-2xl relative z-10">

                {/* Progress Indicators */}
                <div className="flex justify-between mb-8 overflow-x-auto pb-4 px-2 scrollbar-none">
                    {steps.map((step, idx) => {
                        const Icon = step.icon;
                        const isCurrent = currentStep === step.id;
                        const isCompleted = currentStep > step.id;

                        return (
                            <div
                                key={step.id}
                                className="flex flex-col items-center min-w-[80px] group cursor-pointer"
                                onClick={() => handleStepClick(step.id)}
                            >
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center border transition-all duration-300 ${isCurrent ? 'bg-blue-600 border-blue-500 shadow-lg shadow-blue-500/30 scale-110' :

                                    isCompleted ? 'bg-green-500/20 border-green-500/50 text-green-500' :
                                        'bg-white/5 border-white/10 text-gray-600'
                                    }`}>
                                    {isCompleted ? <Check className="h-6 w-6" /> : <Icon className="h-6 w-6" />}
                                </div>
                                <span className={`text-[10px] uppercase font-bold mt-2 whitespace-nowrap transition-colors ${isCurrent ? 'text-blue-400' : isCompleted ? 'text-green-500' : 'text-gray-700'
                                    }`}>
                                    {step.title}
                                </span>
                            </div>
                        );
                    })}
                </div>

                {/* Main Card */}
                <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="bg-gray-900/50 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden shadow-2xl"
                >
                    <div className="p-8 border-b border-white/5 flex justify-between items-center">
                        <div>
                            <h2 className="text-2xl font-bold text-white">{steps[currentStep - 1].title}</h2>
                            <p className="text-gray-400 text-sm">Etapa {currentStep} de {steps.length}</p>
                        </div>
                        <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                            {React.createElement(steps[currentStep - 1].icon, { className: "h-6 w-6 text-blue-400" })}
                        </div>
                    </div>

                    <div className="p-8">
                        {renderStepContent()}

                        {error && (
                            <Alert className="mt-6 bg-red-500/10 border-red-500/20 text-red-400">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}
                    </div>

                    <div className="p-8 bg-black/20 border-t border-white/5 flex gap-4">
                        {currentStep > 1 && (
                            <Button
                                variant="outline"
                                onClick={handlePrevious}
                                disabled={loading}
                                className="flex-1 border-white/10 hover:bg-white/5"
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Voltar
                            </Button>
                        )}
                        <Button
                            onClick={handleNext}
                            disabled={loading}
                            className={`flex-1 ${currentStep === steps.length ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'} text-white font-bold h-12 rounded-xl`}
                        >
                            {loading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <>
                                    {currentStep === steps.length ? 'Concluir Cadastro' : 'Próxima Etapa'}
                                    <ArrowRight className="h-4 w-4 ml-2" />
                                </>
                            )}
                        </Button>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default StaffRegisterPage;
